import React, { useState, useRef, useEffect } from 'react';
import { 
  CheckCircle2, 
  ChevronDown, 
  ChevronUp, 
  User, 
  Calendar,
  Star,
  StarOff,
  MessageCircle
} from 'lucide-react';
import styles from './monthEndCloseManager.module.css';
import CloseNav from '../nav';

// JSON data structure that would come from an API
const apiData = {
  close: {
    id: 1,
    period: "June", // changed from month to period
    year: 2025,
    status: "In Progress",
    progress: 35,
    dueDate: "2025-07-15",
    assignedUserId: 1
  },
  sections: [
    {
      id: 1,
      name: "Pre-Close",
      order: 1,
      completedTasks: 2,
      totalTasks: 3,
      expanded: true,
      tasks: [
        {
          id: 101,
          name: "Request bank statements",
          description: "Contact client for all statements",
          status: "Not Started",
          dueDate: "2025-07-3",
          assignedUserId: 1,
          hasFlag: false,
          priority: true,
          comments: [
            { text: "Requested on 6/10.", date: "2025-06-10T09:00:00Z", unread: false, userId: 1 },
            { text: "Received from client.", date: "2025-06-12T14:00:00Z", unread: false, userId: 4 }
          ]
        },
        {
          id: 102,
          name: "Bank reconciliation setup",
          description: "Prepare reconciliation files",
          status: "Not Started",
          dueDate: "2025-07-03",
          assignedUserId: 1,
          hasFlag: false,
          comments: []
        },
        {
          id: 103,
          name: "Review prior month notes",
          description: "Check for any carryover items",
          status: "In Progress",
          dueDate: "2025-06-28",
          assignedUserId: 2,
          hasFlag: true,
          comments: [
            { text: "Please check the flagged item from last month.", date: "2025-03-30T10:00:00Z", unread: true, userId: 2 }
          ]
        }
      ]
    },
    {
      id: 2,
      name: "Bookkeeping",
      order: 2,
      completedTasks: 1,
      totalTasks: 4,
      expanded: true,
      tasks: [
        {
          id: 201,
          name: "Process banking transactions",
          description: "Enter all bank transactions",
          status: "Done",
          dueDate: "2025-07-05",
          assignedUserId: 1,
          hasFlag: false,
          comments: []
        },
        {
          id: 202,
          name: "Record credit card activity",
          description: "Enter all credit card transactions",
          status: "Not Started",
          dueDate: "2025-07-07",
          assignedUserId: 2,
          hasFlag: false,
          comments: [
            { text: "Waiting for statement upload.", date: "2025-07-05T12:00:00Z", unread: false, userId: 2 }
          ]
        },
        {
          id: 203,
          name: "Process AR/AP items",
          description: "Update accounts receivable and payable",
          status: "Not Started",
          dueDate: "2025-07-08",
          assignedUserId: 2,
          hasFlag: false,
          comments: []
        },
        {
          id: 204,
          name: "Reconcile accounts",
          description: "Ensure all accounts balance",
          status: "Not Started",
          dueDate: "2025-07-09",
          assignedUserId: 1,
          hasFlag: true,
          priority: true,
          comments: [
            { text: "Reminder: check for missing transactions.", date: "2025-07-06T15:00:00Z", unread: false, userId: 1 },
            { text: "Follow up with client if needed.", date: "2025-04-07T09:00:00Z", unread: true, userId: 1 }
          ]
        }
      ]
    },
    {
      id: 3,
      name: "Review",
      order: 3,
      completedTasks: 0,
      totalTasks: 3,
      expanded: false,
      tasks: [
        {
          id: 301,
          name: "Review balance sheet",
          description: "Check all balance sheet accounts",
          status: "Not Started",
          dueDate: "2025-07-10",
          assignedUserId: 3,
          hasFlag: false
        },
        {
          id: 302,
          name: "Review income statement",
          description: "Analyze income statement for inconsistencies",
          status: "Not Started",
          dueDate: "2025-07-11",
          assignedUserId: 3,
          hasFlag: false
        },
        {
          id: 303,
          name: "Verify tax accruals",
          description: "Ensure tax accruals are accurate",
          status: "Not Started",
          dueDate: "2025-07-12",
          assignedUserId: 3,
          hasFlag: false
        }
      ]
    },
    {
      id: 4,
      name: "Adjustments",
      order: 4,
      completedTasks: 0,
      totalTasks: 3,
      expanded: false,
      tasks: []
    },
    {
      id: 5,
      name: "Final Review",
      order: 5,
      completedTasks: 0,
      totalTasks: 3,
      expanded: false,
      tasks: []
    },
    {
      id: 6,
      name: "Client Delivery",
      order: 6,
      completedTasks: 0,
      totalTasks: 3,
      expanded: false,
      tasks: []
    },
    {
      id: 7,
      name: "Post-Close Wrap-Up",
      order: 7,
      completedTasks: 0,
      totalTasks: 3,
      expanded: false,
      tasks: []
    }
  ],
  users: [
    { id: 1, name: "Sarah Wilson" },
    { id: 2, name: "David Lopez" },
    { id: 3, name: "Michelle Taylor" }
  ]
};

export function MonthEndCloseManager() {
  // State to manage section expansion
  const [sections, setSections] = useState(apiData.sections);
  
  // Handle section toggle
  const toggleSection = (sectionId) => {
    setSections(prevSections => 
      prevSections.map(section => 
        section.id === sectionId 
          ? { ...section, expanded: !section.expanded } 
          : section
      )
    );
  };

  // Handle status change
  const handleStatusChange = (sectionId, taskId, newStatus) => {
    setSections(prevSections => 
      prevSections.map(section => {
        if (section.id !== sectionId) return section;
        
        const updatedTasks = section.tasks.map(task => 
          task.id === taskId ? { ...task, status: newStatus } : task
        );
        
        const completedCount = updatedTasks.filter(task => task.status === "Done").length;
        
        return {
          ...section,
          tasks: updatedTasks,
          completedTasks: completedCount
        };
      })
    );
  };

  // Handle assignee change
  const handleAssigneeChange = (sectionId, taskId, userId) => {
    setSections(prevSections => 
      prevSections.map(section => {
        if (section.id !== sectionId) return section;
        const updatedTasks = section.tasks.map(task => 
          task.id === taskId 
            ? { 
                ...task, 
                assignedUserId: parseInt(userId)
              } 
            : task
        );
        return {
          ...section,
          tasks: updatedTasks
        };
      })
    );
  };

  // Ensure all tasks have a 'priority' and 'comments' property
  React.useEffect(() => {
    setSections(prevSections => prevSections.map(section => ({
      ...section,
      tasks: section.tasks.map(task => ({
        priority: false,
        comments: [],
        ...task,
      }))
    })));
    // eslint-disable-next-line
  }, []);

  // Toggle priority for a task
  const handleTogglePriority = (sectionId, taskId) => {
    setSections(prevSections => 
      prevSections.map(section => {
        if (section.id !== sectionId) return section;
        const updatedTasks = section.tasks.map(task => 
          task.id === taskId ? { ...task, priority: !task.priority } : task
        );
        return { ...section, tasks: updatedTasks };
      })
    );
  };

  // Commenting state
  const [commentInput, setCommentInput] = useState("");
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [showCommentPanel, setShowCommentPanel] = useState(false);
  const [commentPanelTask, setCommentPanelTask] = useState(null);

  // Open comment panel for a task
  const openCommentPanel = (sectionId, task) => {
    setCommentPanelTask({ ...task, sectionId });
    setShowCommentPanel(true);
    setCommentInput("");
    setTagSuggestions([]);
  };

  // Close comment panel
  const closeCommentPanel = () => {
    setShowCommentPanel(false);
    setCommentPanelTask(null);
    setCommentInput("");
    setTagSuggestions([]);
  };

  // Handle comment input change and tag suggestions
  const handleCommentInputChange = (e) => {
    const value = e.target.value;
    setCommentInput(value);
    // Tag suggestion logic
    const match = value.match(/@([\w]*)$/);
    if (match) {
      const search = match[1].toLowerCase();
      setTagSuggestions(apiData.users.filter(u => u.name.toLowerCase().includes(search)));
    } else {
      setTagSuggestions([]);
    }
  };

  // Handle selecting a tag suggestion
  const handleTagSuggestionClick = (user) => {
    setCommentInput(prev => prev.replace(/@([\w]*)$/, `@${user.name} `));
    setTagSuggestions([]);
  };



  // Comment panel UI (side panel styled like TransactionCleanup)
  const renderCommentPanel = () => {
    if (!showCommentPanel || !commentPanelTask) return null;
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: 384, // 24rem
        height: '100vh',
        background: '#fff',
        boxShadow: '-2px 0 8px rgba(0,0,0,0.04)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid #e5e7eb',
        transition: 'right 0.2s',
      }}>
        <div style={{ padding: '1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 600, fontSize: '1.125rem', color: '#111827' }}>Comments for: {commentPanelTask.name}</div>
          <button onClick={closeCommentPanel} style={{ background: 'none', border: 'none', borderRadius: 6, padding: 4, fontSize: 22, cursor: 'pointer', color: '#6b7280', transition: 'background 0.2s' }}>&times;</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem', background: '#f9fafb' }}>
          {commentPanelTask.comments && commentPanelTask.comments.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {commentPanelTask.comments.map((c, idx) => (
                <div key={idx} style={{
                  alignSelf: 'flex-start',
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  padding: '10px 14px',
                  maxWidth: '90%',
                  fontSize: '0.97em',
                  color: '#222',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                }}>
                  <div style={{ fontWeight: 600, fontSize: '0.93em', color: '#2563eb', marginBottom: 2 }}>
                    {apiData.users.find(u => u.id === c.userId)?.name || 'Unknown User'}
                  </div>
                  <div>{c.text}</div>
                  <div style={{ color: '#888', fontSize: '0.8em', marginTop: 2 }}>{new Date(c.date).toLocaleString()}</div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: '#888', fontSize: '0.95em' }}>No comments yet.</div>
          )}
        </div>
        <div style={{ borderTop: '1px solid #e5e7eb', padding: '1.5rem', background: '#fff' }}>
          <div style={{ position: 'relative' }}>
            <textarea
              value={commentInput}
              onChange={handleCommentInputChange}
              rows={3}
              style={{ width: '100%', borderRadius: 8, border: '1px solid #d1d5db', padding: 10, marginBottom: 6, fontSize: '1em', background: '#f9fafb' }}
              placeholder="Add a comment... Use @ to tag users"
            />
            {tagSuggestions.length > 0 && (
              <div style={{ position: 'absolute', left: 0, top: '100%', background: '#fff', border: '1px solid #ccc', zIndex: 10, width: '100%' }}>
                {tagSuggestions.map(user => (
                  <div key={user.id} style={{ padding: 8, cursor: 'pointer' }} onClick={() => handleTagSuggestionClick(user)}>
                    @{user.name}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <button onClick={closeCommentPanel} style={{ background: '#f3f4f6', color: '#333', border: 'none', borderRadius: 4, padding: '8px 20px', cursor: 'pointer', fontWeight: 500 }}>Cancel</button>
            <button onClick={() => {
              // Save comment to the correct task in state
              if (!commentInput.trim()) return;
              setSections(prevSections => prevSections.map(section => {
                if (section.id !== commentPanelTask.sectionId) return section;
                const updatedTasks = section.tasks.map(task => {
                  if (task.id !== commentPanelTask.id) return task;
                  return {
                    ...task,
                    comments: [...(task.comments || []), { text: commentInput, date: new Date().toISOString(), userId: task.assignedUserId }]
                  };
                });
                return { ...section, tasks: updatedTasks };
              }));
              setCommentInput("");
              setCommentPanelTask(prev => ({ ...prev, comments: [...(prev.comments || []), { text: commentInput, date: new Date().toISOString(), userId: commentPanelTask.assignedUserId }] }));
            }} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, padding: '8px 20px', cursor: 'pointer', fontWeight: 500 }}>Comment</button>
          </div>
        </div>
      </div>
    );
  };

  // State to track which task's due date is being edited
  const [editingDueDate, setEditingDueDate] = useState({ sectionId: null, taskId: null });

  // Ref for due date input
  const dueDateInputRef = useRef(null);

  // Show calendar widget when input appears
  useEffect(() => {
    if (editingDueDate.sectionId && dueDateInputRef.current) {
      dueDateInputRef.current.showPicker && dueDateInputRef.current.showPicker();
    }
  }, [editingDueDate]);

  // Handler to start editing due date
  const handleEditDueDate = (sectionId, taskId) => {
    setEditingDueDate({ sectionId, taskId });
  };

  // Handler to save due date
  const handleDueDateChange = (sectionId, taskId, newDate) => {
    setSections(prevSections => prevSections.map(section => {
      if (section.id !== sectionId) return section;
      const updatedTasks = section.tasks.map(task =>
        task.id === taskId ? { ...task, dueDate: newDate } : task
      );
      return { ...section, tasks: updatedTasks };
    }));
    setEditingDueDate({ sectionId: null, taskId: null });
  };

  // Helper to render task checkbox based on status
  const renderTaskCheckbox = (status) => {
    switch (status) {
      case "Done":
        return (
          <button className={styles.taskCheckDone}>
            <CheckCircle2 size={16} />
          </button>
        );
      case "In Progress":
        return (
          <button className={styles.taskCheckInProgress}>
            <div className={styles.taskDot} style={{width: '20px'}}></div>
          </button>
        );
      default:
        return (
          <button className={styles.taskCheckNotStarted}>
          </button>
        );
    }
  };


  // Helper to render status dropdown
  const renderStatusDropdown = (section, task) => {
    let className;
    switch (task.status) {
      case "Done":
        className = styles.statusDone;
        break;
      case "In Progress":
        className = styles.statusInProgress;
        break;
      default:
        className = styles.statusNotStarted;
    }

    return (
      <select 
        className={className}
        value={task.status}
        onChange={(e) => handleStatusChange(section.id, task.id, e.target.value)}
      >
        <option value="Not Started">Not Started</option>
        <option value="In Progress">In Progress</option>
        <option value="Done">Done</option>
      </select>
    );
  };

  // Format date from YYYY-MM-DD to MM/DD/YYYY
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
  };


  return (
    <div className={styles.container}>
      <CloseNav />
        {/* Main Content */}
        <div className={styles.mainContent}>
            {/* Header */}
            <header className={styles.headerContainer}>
                <div className={styles.headerInner}>
                    <div className={styles.headerFlex}>
                        
                        <div className={styles.headerFlex}>
                        </div>
                    </div>
                </div>
            </header>
            
            {/* Main content area */}
            <main className={styles.main}>
                {/* Close Detail View */}
                <div>
                    <div className={styles.clientHeader}>
                      <div className={styles.headerFlex}>
                        <div>
                          <h3 className={styles.clientInfo}>
                            {apiData.close.period} {apiData.close.year}
                          </h3>
                          <div className={styles.clientMeta}>
                            <Calendar size={14} className={styles.marginRight4} />
                            <span>Due: {formatDate(apiData.close.dueDate)}</span>
                            <span className={styles.marginRight4}>•</span>
                            <User size={14} className={styles.marginRight4} />
                            <span>
                              {
                                apiData.users.find(u => u.id === apiData.close.assignedUserId)?.name || "Unassigned"
                              }
                            </span>
                          </div>
                        </div>
                        <div className={styles.overallProgress}>
                          <span className={styles.progressLabel}>Overall Progress</span>
                          <div className={styles.sectionHeaderProgressBar}>
                            <div 
                              className={styles.sectionHeaderProgressFill}
                              style={{ width: `${apiData.close.progress}%` }}
                            ></div>
                          </div>
                          <span className={styles.sectionHeaderProgressText}>{apiData.close.progress}%</span>
                          <select className={styles.statusSelect} defaultValue={apiData.close.status} style={{ marginLeft: 16 }}>
                            <option value="Not Started">Not Started</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Completed">Completed</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Sections & Tasks */}
                    <div className={styles.sectionsContainer}>
                        {sections.map(section => (
                            <div key={section.id} className={styles.section}>
                                {/* Section header */}
                                <div className={styles.sectionHeader}>
                                  <div className={styles.sectionHeaderContent}>
                                    <h3 className={styles.sectionTitle}>{section.name}</h3>
                                    <div className={styles.sectionHeaderProgress}>
                                      <div className={styles.sectionHeaderProgressBar}>
                                        <div 
                                          className={styles.sectionHeaderProgressFill}
                                          style={{ width: section.totalTasks > 0 ? `${(section.completedTasks / section.totalTasks) * 100}%` : '0%' }}
                                        ></div>
                                      </div>
                                      <span className={styles.sectionHeaderProgressText}>{section.completedTasks}/{section.totalTasks}</span>
                                    </div>
                                  </div>
                                  <button 
                                    className="text-gray-400 hover:text-gray-600"
                                    onClick={() => toggleSection(section.id)}
                                  >
                                    {section.expanded ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
                                  </button>
                                </div>
                                
                                {/* Tasks table - only show if section is expanded */}
                                {section.expanded && section.tasks.length > 0 && (
                                    <div className={styles.taskList}>
                                        {section.tasks.map(task => {
                                          let taskClass = styles.task;
                                          if (task.status === "Done") {
                                            taskClass += ` ${styles.taskDone}`;
                                          } else {
                                            const dueDate = new Date(task.dueDate);
                                            const now = new Date();
                                            const diffDays = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
                                            if (diffDays < 0) {
                                              taskClass += ` ${styles.taskOverdue}`;
                                            } else if (diffDays <= 3) {
                                              taskClass += ` ${styles.taskDueSoon}`;
                                            }
                                          }
                                          return (
                                            <div key={task.id} className={taskClass}>
                                              <div className={styles.taskCheckboxCol}>
                                                {renderTaskCheckbox(task.status)}
                                              </div>
                                              <div className={styles.taskWidgetsCol}>
                                                <button onClick={() => handleTogglePriority(section.id, task.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', marginLeft: 4 }} aria-label="Toggle Priority">
                                                  {task.priority ? <Star size={16} color="#facc15" fill="#facc15" /> : <StarOff size={16} color="#d1d5db" />}
                                                </button>
                                                {/* {task.hasFlag && (
                                                  <span className={styles.marginRight4}>
                                                    <AlertCircle size={16} />
                                                  </span>
                                                )} */}
                                              </div>
                                              <div className={styles.taskNameCol}>
                                                <h4 className={task.status === "Done" ? styles.taskNameDone : styles.taskName}>{task.name}</h4>
                                                <p className={styles.taskDescription}>{task.description}</p>
                                              </div>
                                              <div className={styles.taskAssigneeCol}>
                                                <select 
                                                  className={styles.assigneeSelect}
                                                  value={task.assignedUserId}
                                                  onChange={(e) => handleAssigneeChange(section.id, task.id, e.target.value)}
                                                >
                                                  {apiData.users.map(user => (
                                                    <option key={user.id} value={user.id}>{user.name}</option>
                                                  ))}
                                                </select>
                                              </div>
                                              <div className={styles.taskDateCol}>
                                                <Calendar size={14} className={styles.marginRight4} />
                                                {editingDueDate.sectionId === section.id && editingDueDate.taskId === task.id ? (
                                                  <input
                                                    type="date"
                                                    ref={dueDateInputRef}
                                                    value={task.dueDate}
                                                    onChange={e => handleDueDateChange(section.id, task.id, e.target.value)}
                                                    onBlur={e => handleDueDateChange(section.id, task.id, e.target.value)}
                                                    autoFocus
                                                    required
                                                    className={styles.formInput}
                                                    style={{ minWidth: 120 }}
                                                  />
                                                ) : (
                                                  <span
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleEditDueDate(section.id, task.id)}
                                                    title="Click to edit due date"
                                                  >
                                                    {formatDate(task.dueDate)}
                                                  </span>
                                                )}
                                              </div>
                                              <div className={styles.taskStatusCol}>
                                                {renderStatusDropdown(section, task)}
                                              </div>
                                              <div className={styles.taskCommentsCol}>
                                                <button onClick={() => openCommentPanel(section.id, { ...task, sectionId: section.id })} style={{ background: 'none', border: 'none', cursor: 'pointer', marginTop: 2, padding: 4, display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }} aria-label="View Comments">
                                                  <span style={{ position: 'relative', display: 'inline-block' }}>
                                                    <MessageCircle size={18} color="#2563eb" />
                                                    {task.comments && task.comments.filter(c => c.unread).length > 0 ? (
                                                      <span className={styles.unreadBadge}>
                                                        {task.comments.filter(c => c.unread).length}
                                                      </span>
                                                    ) : (
                                                      task.comments && task.comments.length > 0 && (
                                                        <span className={styles.totalBadge}>
                                                          {task.comments.length}
                                                        </span>
                                                      )
                                                    )}
                                                  </span>
                                                </button>
                                              </div>
                                            </div>
                                          );
                                        })}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </main>
        </div>
        {renderCommentPanel()}
    </div>
  );
};
